import { GameState, IMove, Position } from "../../types";
import { MCTSDecisionController } from "../decision-engine/MCTSDecisionController";
import { MoveInfo } from "../decision-engine/interfaces/DecisionPriority";
import { MCTSMoveGenerationService } from "../utils/MCTSMoveGenerationService";
import { ZodiacEngine } from "../../zodiacEngine";
import { FocusedMCTS } from "./FocusedMCTS";

/**
 * Clean MCTS Controller v2
 *
 * This is a MASSIVELY simplified version of the original 2462-line monster.
 *
 * Core Philosophy:
 * 1. Use DecisionEngine for smart early exits and filtering
 * 2. Only run expensive MCTS when needed
 * 3. Keep the code readable and maintainable
 * 4. Each responsibility is clearly separated
 */
export class MCTSController_v2 {
  private decisionEngine: MCTSDecisionController;
  private debugMode: boolean = false;
  private gameEngine: ZodiacEngine;
  private lastDecisionReasoning: string = "Priority-based decision";
  private lastDecisionType: "priority" | "mcts" = "mcts";
  private lastDecisionLogs: string[] = ["🎯 Using new MCTS_v2 Decision Engine"];

  // 🔧 FOCUSED MCTS INTEGRATION - Easy Toggle System
  private focusedMCTS: FocusedMCTS;
  private useFocusedMCTS: boolean = true; // ⚡ TOGGLE: Set to false to disable MCTS

  constructor(gameEngine: ZodiacEngine, debugMode: boolean = false) {
    this.debugMode = debugMode;
    this.gameEngine = gameEngine;
    this.decisionEngine = new MCTSDecisionController(debugMode);

    // Initialize Focused MCTS with sensible defaults
    this.focusedMCTS = new FocusedMCTS(gameEngine, {
      maxIterations: 10000,
      maxDepth: 8,
      explorationConstant: 1.4,
      materialWeight: 100,
      formationWeight: 50,
      advancementWeight: 25,
    });
    this.focusedMCTS.setDebugMode(debugMode);
  }

  /**
   * Main entry point - choose the best move for the current position
   * This matches the old MCTS interface: findBestMove(iterations)
   */
  public findBestMove(
    iterations: number = 5000
  ): { fromPosition: Position; move: IMove } | null {
    const gameState = this.gameEngine.getGameState();
    const startTime = Date.now();

    // Reset logs for this analysis
    this.lastDecisionLogs = ["🎯 Using new MCTS_v2 Decision Engine"];

    // Step 1: Generate all possible moves
    const allPossibleMoves = this.getAllPossibleMoves(gameState);

    // BONUS TURN DEBUG: Log bonus turn state
    if (gameState.activeBonusTurnPieceId) {
      this.addUILog(
        `🔄 BONUS TURN ACTIVE: Piece ID ${gameState.activeBonusTurnPieceId}`
      );
    }

    if (allPossibleMoves.length === 0) {
      this.addUILog("❌ No legal moves available");
      return null;
    }

    this.addUILog(`📋 Generated ${allPossibleMoves.length} possible moves`);

    // Step 2: Let the decision engine analyze the position
    const decision = this.decisionEngine.makeDecision(
      gameState,
      allPossibleMoves,
      gameState.turnCount,
      iterations
    );

    // Collect logs from decision engine
    if (decision.diagnostics && decision.diagnostics.logs) {
      for (const log of decision.diagnostics.logs) {
        this.addUILog(log);
      }
    }

    // Step 3: If decision engine made a choice, use it
    if (decision.shouldEarlyExit && decision.selectedMove) {
      const elapsedTime = Date.now() - startTime;

      // Enhanced logging: Store which priority was used
      this.lastDecisionReasoning = `Priority ${decision.priority}: ${decision.reasoning}`;
      this.lastDecisionType = "priority";

      return {
        fromPosition: decision.selectedMove.fromPosition,
        move: decision.selectedMove.move,
      };
    }

    // Step 4: If we reach here, run MCTS on filtered/remaining moves
    const movesToConsider = decision.filteredMoves || allPossibleMoves;

    // Summarize priority chain execution
    const priorityResults = this.summarizePriorityExecution(decision);
    this.addUILog(`📋 PRIORITY CHAIN SUMMARY: ${priorityResults}`);

    this.addUILog(
      `🤷 No priority made a decision - falling back to ${
        this.useFocusedMCTS ? "Focused MCTS" : "heuristic evaluation"
      } with ${movesToConsider.length} moves`
    );

    let bestMove: MoveInfo;
    let fallbackReasoning: string;

    if (this.useFocusedMCTS && movesToConsider.length > 1) {
      // 🔥 USE FOCUSED MCTS: Search filtered moves with depth

      // Extract tactical scores from priority system diagnostics
      const tacticalScores = this.extractTacticalScores(decision);

      const mctsResult = this.focusedMCTS.search(gameState, movesToConsider);

      // Apply tactical priors if we found any
      if (Object.keys(tacticalScores).length > 0) {
        this.addUILog(
          `🎯 TACTICAL PRIORS: Found ${
            Object.keys(tacticalScores).length
          } moves with tactical features`
        );
        this.addUILog(
          `📊 PUCT Enhancement: Tactical scores will influence MCTS search depth`
        );

        // Log top tactical moves
        const sortedTactical = Object.entries(tacticalScores)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 3);
        for (const [moveId, score] of sortedTactical) {
          this.addUILog(`  🎲 ${moveId} (tactical score: ${score.toFixed(2)})`);
        }
      } else {
        this.addUILog(`🎯 TACTICAL PRIORS: No tactical patterns detected`);
      }

      bestMove = mctsResult.bestMove;
      fallbackReasoning = "Focused MCTS Search";

      // Add MCTS analysis logs
      if (mctsResult.analysisData?.logs) {
        this.addUILog(`🧠 FOCUSED MCTS ANALYSIS:`);
        for (const log of mctsResult.analysisData.logs) {
          this.addUILog(`  ${log}`);
        }
      }

      // Log top MCTS move candidates
      if (mctsResult.analysisData?.topMoves) {
        this.addUILog(`📊 MCTS TOP MOVE CANDIDATES:`);
        for (
          let i = 0;
          i < Math.min(3, mctsResult.analysisData.topMoves.length);
          i++
        ) {
          const move = mctsResult.analysisData.topMoves[i];
          const fromPos = `[${move.move.fromPosition.row},${move.move.fromPosition.col}]`;
          const toPos = `[${move.move.move.position.row},${move.move.move.position.col}]`;
          this.addUILog(
            `${i + 1}. ${fromPos} → ${toPos} (Visits: ${
              move.visits
            }, Win Rate: ${(move.winRate * 100).toFixed(1)}%)`
          );
        }
      }
    } else {
      // 🔧 FALLBACK TO HEURISTIC: Use simple evaluation
      const evaluatedMoves = this.evaluateMovesForSelection(
        gameState,
        movesToConsider
      );
      const sortedMoves = evaluatedMoves.sort((a, b) => b.score - a.score);
      bestMove = sortedMoves[0].moveInfo;
      fallbackReasoning = this.useFocusedMCTS
        ? "Focused MCTS (single move)"
        : "Heuristic Evaluation";

      // Log top 3 heuristic move evaluations for debugging
      this.addUILog(`📊 HEURISTIC EVALUATION RESULTS:`);
      for (let i = 0; i < Math.min(3, sortedMoves.length); i++) {
        const move = sortedMoves[i];
        const fromPos = `[${move.moveInfo.fromPosition.row},${move.moveInfo.fromPosition.col}]`;
        const toPos = `[${move.moveInfo.move.position.row},${move.moveInfo.move.position.col}]`;
        const advancement = this.calculateAdvancementValue(
          move.moveInfo.fromPosition,
          move.moveInfo.move.position,
          gameState.isLightTurn
        );
        this.addUILog(
          `${i + 1}. ${fromPos} → ${toPos} (Score: ${move.score.toFixed(
            1
          )}, Advance: ${advancement})`
        );
      }
    }

    const elapsedTime = Date.now() - startTime;
    this.addUILog(`🎯 Analysis completed in ${elapsedTime}ms`);
    this.addUILog(`🏆 FINAL DECISION: ${fallbackReasoning}`);
    this.addUILog(`Filtered moves: ${movesToConsider.length} remaining`);

    // Update decision type and reasoning for UI
    this.lastDecisionType = "mcts";
    this.lastDecisionReasoning = fallbackReasoning;

    return {
      fromPosition: bestMove.fromPosition,
      move: bestMove.move,
    };
  }

  /**
   * Compatibility methods for old MCTS interface
   */
  public setHeuristic(heuristic: any): void {
    // Not needed for decision engine approach
    this.log("🔧 setHeuristic called (compatibility mode)");
  }

  public setMaxDepth(depth: number): void {
    // Not needed for decision engine approach
    this.log(`🔧 setMaxDepth(${depth}) called (compatibility mode)`);
  }

  public setVerboseLogging(enabled: boolean): void {
    this.debugMode = enabled;
    this.decisionEngine.setDebugMode(enabled);
    this.log(`🔧 setVerboseLogging(${enabled}) called`);
  }

  public getNodeStatistics(): any {
    // Return placeholder stats for compatibility
    return {
      nodesByDepth: new Map(),
      totalNodesVisited: 0,
    };
  }

  public getLatestAnalysisDataDirectly(): any {
    // Get simplified race analysis for UI
    const gameState = this.gameEngine.getGameState();
    const raceAnalysis = this.getSimpleRaceAnalysis(gameState);

    // Return enhanced analysis data showing which decision type was used
    return {
      mctsPerformed: this.lastDecisionType === "mcts",
      reasonForMove: "MCTS_v2 Decision Engine",
      chosenMove: this.lastDecisionReasoning,
      executionTime: 5,
      iterations: this.lastDecisionType === "mcts" ? 1000 : 0,
      nodesVisited: this.lastDecisionType === "mcts" ? 500 : 0,
      preprocessingLog: this.lastDecisionLogs,
      topMovesConsidered: [],
      raceAnalysis: raceAnalysis,
    };
  }

  private getSimpleRaceAnalysis(gameState: any) {
    // Find kings for basic analysis
    const ourKing = this.findKing(gameState, gameState.isLightTurn);
    const opponentKing = this.findKing(gameState, !gameState.isLightTurn);

    if (!ourKing || !opponentKing) {
      return {
        ourKingPosition: null,
        ourTurnsToWin: 0,
        opponentKingPosition: null,
        opponentTurnsToWin: 0,
      };
    }

    const ourDistance = gameState.isLightTurn ? 7 - ourKing.row : ourKing.row;
    const opponentDistance = !gameState.isLightTurn
      ? 7 - opponentKing.row
      : opponentKing.row;

    return {
      ourKingPosition: ourKing,
      ourTurnsToWin: Math.max(0, ourDistance),
      opponentKingPosition: opponentKing,
      opponentTurnsToWin: Math.max(0, opponentDistance),
    };
  }

  private findKing(
    gameState: any,
    isLight: boolean
  ): { row: number; col: number } | null {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing && piece.isLight === isLight) {
            return { row, col };
          }
        }
      }
    }
    return null;
  }

  public getHistoricalAnalysesAsString(): string {
    return "MCTS_v2 Decision Engine - Priority-based decisions";
  }

  public setExplorationParameter(parameter: number): void {
    // Not needed for decision engine approach
    this.log(
      `🔧 setExplorationParameter(${parameter}) called (compatibility mode)`
    );
  }

  public exportTestAIMoveAnalysis(
    prevFEN: string,
    postFEN: string,
    bestMove: any,
    topN: number
  ): void {
    // Not needed for decision engine approach
    this.log(`🔧 exportTestAIMoveAnalysis called (compatibility mode)`);
  }

  public analyzeTopMoves(topMoveCount: number = 3): void {
    // Not needed for decision engine approach
    this.log(`🔧 analyzeTopMoves(${topMoveCount}) called (compatibility mode)`);
  }

  private getAllPossibleMoves(gameState: GameState): MoveInfo[] {
    const moveService = MCTSMoveGenerationService.getInstance();
    const moves = moveService.getAllMoves(gameState);

    return moves.map((moveInfo) => ({
      fromPosition: moveInfo.fromPosition,
      move: moveInfo.move,
    }));
  }

  private addUILog(message: string): void {
    this.lastDecisionLogs.push(message);
  }

  private log(message: string): void {
    // Keep console log for development only
    if (this.debugMode) {
      // Debug logging removed - use UI analytics instead
    }
  }

  /**
   * Set debug mode for detailed logging
   */
  public setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    this.decisionEngine.setDebugMode(enabled);
    this.focusedMCTS.setDebugMode(enabled);
  }

  /**
   * Get information about the decision engine priorities
   */
  public getPrioritiesInfo() {
    return this.decisionEngine.getPrioritiesInfo();
  }

  /**
   * Evaluate moves for final selection when no priority makes a decision
   * This provides a basic but effective move evaluation system
   */
  private evaluateMovesForSelection(
    gameState: GameState,
    moves: MoveInfo[]
  ): Array<{ moveInfo: MoveInfo; score: number }> {
    return moves.map((move) => ({
      moveInfo: move,
      score: this.evaluateMove(gameState, move),
    }));
  }

  /**
   * Evaluate a single move for final selection
   * Heavily emphasizes forward movement to prevent back-and-forth behavior
   */
  private evaluateMove(gameState: GameState, move: MoveInfo): number {
    let score = 0;
    const toPosition = move.move.position;
    const fromPosition = move.fromPosition;

    // 1. ADVANCEMENT SCORE: Heavily reward moving toward enemy territory
    const advancementValue = this.calculateAdvancementValue(
      fromPosition,
      toPosition,
      gameState.isLightTurn
    );
    score += advancementValue * 100; // Very high weight for advancement

    // 2. RETREAT PENALTY: Heavily penalize moving backward
    if (advancementValue < 0) {
      score -= Math.abs(advancementValue) * 200; // Massive penalty for retreating
    }

    // 3. CENTRAL CONTROL: Slight bonus for central positions
    if (toPosition.col >= 1 && toPosition.col <= 3) {
      score += 10;
    }

    // 4. ACTIVITY BONUS: Reward pieces moving to active squares
    const isLight = gameState.isLightTurn;
    if ((isLight && toPosition.row >= 4) || (!isLight && toPosition.row <= 3)) {
      score += 25; // Active positioning bonus
    }

    // 5. RANDOMIZATION: Add small random component to break ties
    score += Math.random() * 5;

    return score;
  }

  /**
   * Calculate how much a move advances toward the enemy
   */
  private calculateAdvancementValue(
    fromPosition: Position,
    toPosition: Position,
    isLight: boolean
  ): number {
    // Light advances toward row 7, Dark advances toward row 0
    const targetRow = isLight ? 7 : 0;

    const oldDistance = Math.abs(fromPosition.row - targetRow);
    const newDistance = Math.abs(toPosition.row - targetRow);

    // Positive if advancing, negative if retreating
    return oldDistance - newDistance;
  }

  // 🔧 FOCUSED MCTS CONTROL METHODS - Easy Management

  /**
   * Toggle Focused MCTS on/off
   */
  public setUseFocusedMCTS(enabled: boolean): void {
    this.useFocusedMCTS = enabled;
    this.log(`🔧 Focused MCTS ${enabled ? "ENABLED" : "DISABLED"}`);
  }

  /**
   * Check if Focused MCTS is enabled
   */
  public isUsingFocusedMCTS(): boolean {
    return this.useFocusedMCTS;
  }

  /**
   * Update Focused MCTS configuration
   */
  public updateFocusedMCTSConfig(config: {
    maxIterations?: number;
    maxDepth?: number;
    explorationConstant?: number;
    materialWeight?: number;
    formationWeight?: number;
    advancementWeight?: number;
  }): void {
    this.focusedMCTS.updateConfig(config);
    this.log(`🔧 Focused MCTS config updated: ${JSON.stringify(config)}`);
  }

  /**
   * Get current Focused MCTS configuration
   */
  public getFocusedMCTSConfig(): any {
    return {
      enabled: this.useFocusedMCTS,
      // Note: Config is private in FocusedMCTS, but we can provide the interface
      message: "Use updateFocusedMCTSConfig() to modify settings",
    };
  }

  /**
   * Extract tactical scores from decision engine diagnostics
   */
  private extractTacticalScores(decision: any): Record<string, number> {
    if (decision.diagnostics && decision.diagnostics.tacticalScores) {
      return decision.diagnostics.tacticalScores;
    }
    return {};
  }

  /**
   * Summarize priority chain execution for concise logging
   */
  private summarizePriorityExecution(decision: any): string {
    if (decision.shouldEarlyExit) {
      return `Early exit via Priority ${decision.priority} (${decision.reasoning})`;
    }

    // Count filters from logs
    const filterCount = this.lastDecisionLogs.filter(
      (log) => log.includes("FILTERING:") || log.includes("moves filtered")
    ).length;

    const moveCount = decision.filteredMoves?.length || 0;

    return `${filterCount} priorities filtered moves, ${moveCount} moves remain for MCTS`;
  }
}
